import {
  IKvitteringData,
  kvitteringStatusEnum,
} from '../../../ducks/kvittering/types'
import { queryClient } from '../../../api/query-client'
import { Draft, Immutable, produce } from 'immer'
import { mergeKeyWith } from '../../../common/query'

const getKvitteringKeys = () => {
  const keys = {
    all: [{ entity: 'kvitteringer' }] as const,
    kvitteringId: (kvitteringId: string) =>
      mergeKeyWith(keys.all, { kvitteringId }),
    sammenstilling: (kvitteringId: string, url: string) =>
      mergeKeyWith(keys.all, { type: 'sammenstilling', kvitteringId, url }),
    tilsynstyper: () => mergeKeyWith(keys.all, { type: 'tilsynstyper' }),
    forsideKvitteringer: () =>
      mergeKeyWith(keys.all, { type: 'forside-kvitteringer' }),
    create: () => mergeKeyWith(keys.all, { action: 'create' }),
    update: () => mergeKeyWith(keys.all, { action: 'update' }),
    debouncedUpdate: () =>
      mergeKeyWith(keys.all, { action: 'debounced-update' }),
    delete: (kvitteringId: string) =>
      mergeKeyWith(keys.all, { action: 'delete', kvitteringId }),
    fordel: (kvitteringId: string) =>
      mergeKeyWith(keys.all, { action: 'fordel-til-kollega', kvitteringId }),
    hent: () => mergeKeyWith(keys.all, { action: 'hent-fra-kollega' }),
    arkiver: (kvitteringId: string) =>
      mergeKeyWith(keys.all, { action: 'arkiver', kvitteringId }),
    ferdigstill: (kvitteringId: string) =>
      mergeKeyWith(keys.all, { action: 'ferdigstill', kvitteringId }),
  } as const

  return keys
}

const kvitteringKeys = getKvitteringKeys()

export type KvitteringKeys = typeof kvitteringKeys

export { kvitteringKeys, kvitteringQueryHelpers }

const kvitteringQueryHelpers = {
  getKvitteringCache: (kvitteringId: string): Immutable<IKvitteringData> => {
    const kvittering = queryClient.getQueryData<Immutable<IKvitteringData>>(
      kvitteringKeys.kvitteringId(kvitteringId)
    )

    if (!kvittering) {
      throw new Error(`Kvittering ${kvitteringId} not found in cache`)
    }

    return kvittering
  },
  getForsideKvitteringerCache: (
    filter?: (data: IKvitteringData[]) => Immutable<IKvitteringData[]>
  ): Immutable<IKvitteringData[]> => {
    const kvitteringer = queryClient.getQueryData<IKvitteringData[]>(
      kvitteringKeys.forsideKvitteringer()
    )

    if (filter) return filter(kvitteringer ?? [])

    return kvitteringer ?? []
  },
  updateKvitteringCache: (
    kvitteringId: string,
    updater: (draft: Draft<IKvitteringData>) => void
  ) => {
    queryClient.setQueryData<IKvitteringData>(
      kvitteringKeys.kvitteringId(kvitteringId),
      (kvittering) => {
        if (!kvittering) {
          return kvittering
        }

        return produce(kvittering, (draft) => updater(draft))
      }
    )
  },
  updateForsideKvitteringerCache: (
    updater: (draft: Draft<IKvitteringData[]>) => void
  ) => {
    queryClient.setQueryData<IKvitteringData[]>(
      kvitteringKeys.forsideKvitteringer(),
      (kvitteringer) => {
        if (!kvitteringer) return kvitteringer

        return produce(kvitteringer, (draft) => updater(draft))
      }
    )
  },
  updateCacheOnArchive: (
    kvitteringId: string,
    status: ITilsynskvitteringRemote.TilsynskvitteringDTO['status'],
    sak: string
  ) => {
    queryClient.setQueryData<IKvitteringData>(
      kvitteringKeys.kvitteringId(kvitteringId),
      (kvittering) => {
        if (!kvittering) {
          return kvittering
        }

        return produce(kvittering, (draft: Draft<IKvitteringData>) => {
          const [aar, sekvensnummer] = sak.split('/')
          draft.status = status
          draft.noarksakAar = aar
          draft.noarksakSekvensnummer = sekvensnummer
        })
      }
    )
  },
  invalidateKvittering: (kvitteringId: string) =>
    queryClient.invalidateQueries({
      queryKey: kvitteringKeys.kvitteringId(kvitteringId),
    }),
  invalidateForsideKvitteringer: () =>
    queryClient.invalidateQueries({
      queryKey: kvitteringKeys.forsideKvitteringer(),
    }),
  cancelKvitteringQuery: (kvitteringId: string) =>
    queryClient.cancelQueries({
      queryKey: kvitteringKeys.kvitteringId(kvitteringId),
    }),
}

export const getForsideKvitteringLists = (kvitteringer: IKvitteringData[]) => {
  const paaBegyntKvitteringer =
    kvitteringer?.filter((k) => k.status == kvitteringStatusEnum.PAABEGYNT) ??
    []

  const forberedtKvitteringer =
    kvitteringer?.filter((k) => k.status == kvitteringStatusEnum.FORBEREDT) ??
    []

  const ferdigstiltKvitteringer =
    kvitteringer?.filter(
      (k) =>
        k.status == kvitteringStatusEnum.FERDIGSTILT ||
        k.status == kvitteringStatusEnum.FERDIGSTILLING_PAAGAAR
    ) ?? []

  const arkivertKvitteringer =
    kvitteringer?.filter(
      (k) =>
        k.status == kvitteringStatusEnum.ARKIVERT ||
        k.status == kvitteringStatusEnum.ARKIVERING_PAAGAAR
    ) ?? []

  const feiletKvitteringer =
    kvitteringer?.filter(
      (k) =>
        k.status == kvitteringStatusEnum.ARKIVERING_PDF_FEILET ||
        k.status == kvitteringStatusEnum.ARKIVERING_VEDLEGG_FEILET ||
        k.status == kvitteringStatusEnum.FERDIGSTILLING_PDF_FEILET ||
        k.status == kvitteringStatusEnum.FERDIGSTILLING_VEDLEGG_FEILET
    ) ?? []

  return {
    paaBegyntKvitteringer,
    forberedtKvitteringer,
    ferdigstiltKvitteringer,
    arkivertKvitteringer,
    feiletKvitteringer,
  }
}
